import { SIZES, BUILD } from '@consts/index'
import { isEmptyObject, objectKeys } from '@helpers/utils'
import trackError from '@helpers/trackError'
import { upperFirstLetter } from '@helpers/utils'
import { getStorageValue, setStoredValue } from '@helpers/hooks/useStorage'
import { isBrowser } from '@helpers/env'
import { Fit } from 'types/fit'
import { FIT_STORAGE_KEYS } from '@helpers/hooks/useFit'

export function parseQuery(queryString: string): Record<string, string> {
  const query: Record<string, string> = {}
  const pairs = (
    queryString[0] === '?' ? queryString.substr(1) : queryString
  ).split('&')
  for (let i = 0; i < pairs.length; i++) {
    const pair = decodeURIComponent(
      pairs[i].replace(/%([^\d{2}][\u2013|\u2014])/g, '$1')
    ).split('=')
    query[pair[0]] = pair[1] || ''
  }
  return query
}

type FitKey = Lowercase<Extract<keyof Fit, string>> | 'shortsleg'
type FitRecord = Partial<Record<FitKey, string>>

type FitFinderMap = Record<string, FitKey>

const FIT_SIZES_MAP: FitFinderMap = {
  sl: 'shortsleg',
  b: 'build',
  l: 'leg',
  w: 'waist',
  s: 'size',
  c: 'cut',
  lh: 'length'
}

function getFitFromUrl(): FitRecord {
  if (!isBrowser()) return {}

  const urlParams = parseQuery(window.location.search)

  return objectKeys(urlParams).reduce<Partial<Record<FitKey, string>>>(
    (fit, param) => {
      const fitKey = FIT_SIZES_MAP[param]
      if (!fitKey) return fit

      const paramValue = urlParams[param]
      const formattedValue = upperFirstLetter(paramValue)

      const isValidFit = SIZES[fitKey]?.some(
        fitType =>
          fitType.replace(BUILD, '').trim().toLowerCase() ===
          paramValue.toLowerCase()
      )

      if (isValidFit) {
        return {
          ...fit,
          [fitKey]: formattedValue
        }
      }

      return fit
    },
    {}
  )
}

const FIT_SIZE_URL_PARAMS = new Set(objectKeys(FIT_SIZES_MAP))

// Any query param that isn't a fit-size shortcode (w, l, b, s, c, lh, sl) is
// treated as a facet filter, keyed by its own name (colour, fabric, or any
// facet type added later) — so new facets need no changes here.
function getFacetFiltersFromUrl(): Partial<Record<string, string[]>> {
  if (!isBrowser()) return {}

  const urlParams = parseQuery(window.location.search)

  return objectKeys(urlParams).reduce<Partial<Record<string, string[]>>>(
    (acc, param) => {
      if (FIT_SIZE_URL_PARAMS.has(param)) return acc

      const rawValue = urlParams[param]
      if (!rawValue) return acc

      const values = [
        ...new Set(
          rawValue
            .split(',')
            .map(value => value.trim().toLowerCase())
            .filter(Boolean)
        )
      ]

      return values.length ? { ...acc, [param]: values } : acc
    },
    {}
  )
}

function createFitObject(
  fitFromUrl: Partial<Record<string, string>>,
  parsedCookie: Partial<Record<string, string>> = {}
): Partial<Record<string, string>> {
  if (!isEmptyObject(fitFromUrl)) {
    return fitFromUrl
  }

  return parsedCookie
}

export function initFit(): Partial<Record<string, string>> {
  try {
    const sessionFilters = getStorageValue<
      Partial<Record<string, string | string[]>>
    >('sessionStorage', FIT_STORAGE_KEYS.FILTERS, {})
    const fitFromUrl = getFitFromUrl()
    const facetFiltersFromUrl = getFacetFiltersFromUrl()
    const fit = createFitObject(
      fitFromUrl,
      sessionFilters as Partial<Record<string, string>>
    )
    const newFilters: FitRecord = objectKeys(fit || {}).reduce<FitRecord>(
      (json, item) => {
        const type = item.replace('swimshortsleg', 'shortsleg')
        const size = SIZES[type.toLowerCase() as keyof typeof SIZES]
        const arrayToCrawl =
          type.toLowerCase() === 'shortsleg'
            ? [...new Set([...SIZES.shortsleg, ...size])]
            : size

        const isValidFitSizeName =
          !!size &&
          arrayToCrawl.findIndex((value: string) => {
            return (
              value.toLowerCase().replace('build ', '') ===
              ((fit || {})[type] || '').toString().toLowerCase()
            )
          }) > -1
        return !isValidFitSizeName
          ? json
          : { ...json, [type.toLowerCase()]: (fit || {})[type] }
      },
      {}
    )

    if (!isEmptyObject(fitFromUrl) || !isEmptyObject(facetFiltersFromUrl)) {
      const sizeKeys = new Set([
        ...objectKeys(SIZES),
        'shortsleg',
        'swimshortsleg'
      ])
      const nonSizeSessionFilters = objectKeys(sessionFilters).reduce<
        Partial<Record<string, string | string[]>>
      >((acc, key) => {
        if (sizeKeys.has(key.toLowerCase())) return acc
        return { ...acc, [key]: sessionFilters[key] }
      }, {})

      const { shortsleg, ...rest } = newFilters
      const filtersToStore: Partial<Record<string, string | string[]>> & {
        shortsLeg?: string | number
      } = {
        ...nonSizeSessionFilters,
        ...rest,
        ...facetFiltersFromUrl,
        ...(shortsleg !== undefined ? { shortsLeg: shortsleg } : {})
      }
      setStoredValue('sessionStorage', FIT_STORAGE_KEYS.FILTERS, filtersToStore)
    }

    return newFilters
  } catch (error) {
    const sessionFilters = getStorageValue(
      'sessionStorage',
      FIT_STORAGE_KEYS.FILTERS,
      '{}'
    )

    setStoredValue('sessionStorage', FIT_STORAGE_KEYS.FILTERS, {})

    trackError(new Error('error on filter sessionStorage parsing'), {
      error: error as Error,
      sessionFilters
    })
    return {}
  }
}
